/**
 * Draws the connecting line and each node's icon once revealed — same "wait
 * for goodface:revealed, then animate with the Web Animations API" shape as
 * the course-hero exhibit's checkmark. Skipped under reduced motion; both the
 * line and the icons are fully painted at rest, so nothing depends on this
 * having run.
 */
import { reducedMotion } from '../../src/js/utils/motion';

const drawShape = (shape: SVGGeometryElement): (() => void) => {
  const length = shape.getTotalLength();

  shape.style.strokeDasharray = `${length}`;
  shape.style.strokeDashoffset = `${length}`;

  return () => {
    shape.animate(
      { strokeDashoffset: [`${length}`, '0'] },
      { duration: 600, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
    );
  };
};

const setupIcon = (node: HTMLElement, delayMs: number): void => {
  const shapes = node.querySelectorAll<SVGGeometryElement>('[data-framework-icon] path, [data-framework-icon] circle');

  if (!shapes.length) {
    return;
  }

  const draws = Array.from(shapes).map(drawShape);

  const run = (): void => {
    window.setTimeout(() => draws.forEach((draw) => draw()), delayMs);
  };

  if (node.classList.contains('is-revealed')) {
    run();
    return;
  }

  node.addEventListener('goodface:revealed', run, { once: true });
};

const setupLine = (row: HTMLElement): void => {
  const line = row.querySelector<SVGPathElement>('[data-framework-line]');
  const firstNode = row.querySelector<HTMLElement>('.framework__node');

  if (!line || !firstNode) {
    return;
  }

  const draw = drawShape(line);

  if (firstNode.classList.contains('is-revealed')) {
    draw();
    return;
  }

  firstNode.addEventListener('goodface:revealed', draw, { once: true });
};

const setup = (row: HTMLElement): void => {
  if (reducedMotion()) {
    return;
  }

  setupLine(row);

  row.querySelectorAll<HTMLElement>('.framework__node').forEach((node, index) => {
    setupIcon(node, index * 90);
  });
};

const init = (): void => {
  document.querySelectorAll<HTMLElement>('[data-framework]').forEach(setup);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

export {};
